import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { Card, Col, Container, ContainerWithPadding, defaultLabelSort, FormFieldLabel, FormFieldWrapper, Grid, PInTableList, Row, SectionTitle, SelectFormField, SelectOptionModel } from 'traveldesk-ui'
import { ApplicationState } from 'traveldesk-ui/dist/store'
import { ApplicationState as LocalState } from '../../store'
import { actionCreators as toursCategoriesActions } from '../../store/ToursCategories'

import ExternalIntegrationsApiService from '../../services/ExternalIntegrationsApiService'
import { BookingType, BookingTypeOptions } from '../../models/OfferBookingType'
import { OfferPrivateType, OfferPrivateTypeOptions } from '../../models/OfferPrivateType'
import { CustomInputImportModel, CustomInputPer, CustomInputPerOptions, CustomInputType, OfferImportModel, TourImportModel } from './TourImportModel'
import ToursApiService from '../../services/ToursApiService'

interface Props {

}

const truncate = (text: string | undefined, max: number) => {
    if (!text) return ''
    const t = text.trim()
    return t.length <= max ? t : `${t.slice(0, max)}…`
}

const formatMoney = (n: number | undefined | null) =>
    n == null || Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })

/** Interprets slot times as minutes from midnight (common API pattern). */
const formatMinutesOfDay = (m: number | undefined) => {
    if (m == null || m < 0) return '—'
    const h = Math.floor(m / 60) % 24
    const min = m % 60
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')} (${m})`
}

const privateTypeLabel = (v: OfferPrivateType | undefined) =>
    v === undefined ? '—' : (OfferPrivateTypeOptions.find(o => o.value === v)?.label ?? String(v))

const bookingTypeLabel = (v: BookingType | undefined) =>
    v === undefined ? '—' : (BookingTypeOptions.find(o => o.value === v)?.label ?? String(v))

const customInputTypeLabel = (v: CustomInputType | undefined) =>
    v === undefined ? '—' : (CustomInputType[v] ?? String(v))

const customInputPerLabel = (v: CustomInputPer | undefined) =>
    v === undefined ? '—' : (CustomInputPerOptions.find(o => o.value === v)?.label ?? String(v))

const OfferImportDetail: React.FC<{ offer: OfferImportModel; index: number; getString: (key: string) => string }> = ({
    offer,
    index,
    getString,
}) => {
    const title = offer.nameDisplay || offer.externalId || `${getString('Offer')} ${index + 1}`
    const slots = offer.timeSlots ?? []
    const periods = offer.pricingPeriods ?? []
    const customs = offer.customFields ?? []
    const customInputs = offer.customInputs ?? []
    const translations = offer.translations ?? []
    const origins = offer.originIds ?? []

    return (
        <div
            style={{
                marginTop: 12,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 6,
                background: 'rgba(0,0,0,0.02)',
            }}
        >
            <PInTableList fontSize="15px" style={{ marginBottom: 8 }}>
                <strong>{title}</strong>
            </PInTableList>
            <PInTableList fontSize="13px">
                <div><strong>{getString('External ID')}</strong>: {offer.externalId ?? '—'}</div>
                <div><strong>{getString('Private / sharing')}</strong>: {privateTypeLabel(offer.privateType)}</div>
                <div><strong>{getString('Booking type')}</strong>: {bookingTypeLabel(offer.bookingType)}</div>
                <div>
                    <strong>{getString('Guests')}</strong>: {offer.minNumOfPeople ?? '—'} – {offer.maxNumOfPeople ?? '—'}
                </div>
                <div>
                    <strong>{getString('Duration')}</strong>:{' '}
                    {offer.numberOfHours ?? 0}h {offer.numberOfMinutes ?? 0}m ·{' '}
                    <strong>{getString('Min. advance booking')}</strong> {offer.minimumAdvancedBooking ?? '—'}
                </div>
                <div>
                    <strong>{getString('UTC offset/Time zone')}</strong>: {offer.utcTimeShift ?? '—'} / {offer.timeZone ?? '—'}
                </div>
                <div>
                    <strong>{getString('Direct reporting')}</strong>:{' '}
                    {offer.isDirectReporting === undefined ? '—' : offer.isDirectReporting ? getString('Yes') : getString('No')}
                </div>
                <div>
                    <strong>{getString('Children')}</strong>:{' '}
                    {offer.childrenAllowed === undefined ? '—' : offer.childrenAllowed ? getString('Yes') : getString('No')}
                    {offer.childAge != null && ` (${getString('max age')} ${offer.childAge})`}
                </div>
                <div>
                    <strong>{getString('Infants')}</strong>:{' '}
                    {offer.infantsAllowed === undefined ? '—' : offer.infantsAllowed ? getString('Yes') : getString('No')}
                    {offer.infantAge != null && ` (${getString('max age')} ${offer.infantAge})`}
                </div>
                {origins.length > 0 && (
                    <div>
                        <strong>{getString('Origin IDs')}</strong>: {origins.join(', ')}
                    </div>
                )}
            </PInTableList>
            {offer.description && (
                <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 13 }}>
                    {truncate(offer.description, 400)}
                </p>
            )}
            {customs.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Custom fields')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {customs.map((cf, i) => (
                            <li key={`${cf.fieldName}-${i}`}>
                                <strong>{cf.fieldName}</strong>: {cf.value ?? '—'}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {customInputs.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Custom inputs')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {customInputs.map((ci, i) => (
                            <li key={`${ci.externalId ?? ''}-${i}`} style={{ marginBottom: 6 }}>
                                <strong>{ci.name ?? '—'}</strong>{' '}
                                <span style={{ opacity: 0.7 }}>
                                    [{customInputTypeLabel(ci.type)}] [{customInputPerLabel(ci.per)}]
                                </span>
                                {ci.isRequired && <span style={{ color: '#d32f2f', marginLeft: 4 }}>*{getString('Required')}</span>}
                                {ci.isAddon && <span style={{ color: '#1976d2', marginLeft: 4 }}>({getString('Addon')})</span>}
                                {ci.description && <div style={{ opacity: 0.7 }}>{truncate(ci.description, 200)}</div>}
                                <div>
                                    {ci.minNumber != null && <span>{getString('Min')}: {ci.minNumber} </span>}
                                    {ci.maxNumber != null && <span>{getString('Max')}: {ci.maxNumber} </span>}
                                    {ci.price != null && <span>{getString('Price')}: {ci.price} </span>}
                                </div>
                                {ci.items && ci.items.length > 0 && (
                                    <ul style={{ paddingLeft: 16, margin: '2px 0 0' }}>
                                        {ci.items.map((item, j) => (
                                            <li key={`${item.value ?? ''}-${j}`}>
                                                {item.label ?? '—'} = {item.value ?? '—'}
                                                {item.price != null && ` (${getString('Price')}: ${item.price})`}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {translations.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Translations')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {translations.map((tr, i) => (
                            <li key={`${tr.languageCode}-${i}`}>
                                [{tr.languageCode}] {tr.nameDisplay ?? '—'}
                                {tr.description && ` — ${truncate(tr.description, 120)}`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {slots.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Time slots')}</strong>
                    <table className="table table-condensed" style={{ marginTop: 6, fontSize: 12, width: '100%' }}>
                        <thead>
                            <tr>
                                <th>{getString('External ID')}</th>
                                <th>{getString('Start')}</th>
                                <th>{getString('End')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {slots.map((s, i) => (
                                <tr key={s.externalId ?? `slot-${i}`}>
                                    <td>{s.externalId ?? '—'}</td>
                                    <td>{formatMinutesOfDay(s.startTime)}</td>
                                    <td>{formatMinutesOfDay(s.endTime)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {periods.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong style={{ fontSize: 13 }}>{getString('Pricing periods')}</strong>
                    <div style={{ overflowX: 'auto', marginTop: 6 }}>
                        <table className="table table-condensed" style={{ fontSize: 11, width: '100%', minWidth: 480 }}>
                            <thead>
                                <tr>
                                    <th>{getString('From')}</th>
                                    <th>{getString('To')}</th>
                                    <th>{getString('Adult')}</th>
                                    <th>{getString('Child')}</th>
                                    <th>{getString('Trip')}</th>
                                    <th>{getString('Net adult')}</th>
                                    <th>{getString('Net child')}</th>
                                    <th>{getString('Net trip')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {periods.map((p, i) => (
                                    <tr key={`${p.periodStart}-${i}`}>
                                        <td>{p.periodStart ?? '—'}</td>
                                        <td>{p.periodEnd ?? '—'}</td>
                                        <td>{formatMoney(p.recPricePerAdultBaseCurrency)}</td>
                                        <td>{formatMoney(p.recPricePerChildBaseCurrency)}</td>
                                        <td>{formatMoney(p.recPricePerTripBaseCurrency)}</td>
                                        <td>{formatMoney(p.netPricePerAdultBaseCurrency)}</td>
                                        <td>{formatMoney(p.netPricePerChildBaseCurrency)}</td>
                                        <td>{formatMoney(p.netPricePerTripBaseCurrency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

const TourImportPreview: React.FC<{ tour: TourImportModel; getString: (key: string) => string; toursCategoriesHash: Map<number, string> }> = ({ tour, getString, toursCategoriesHash }) => {
    const photoCount = tour.photos?.length ?? 0
    const offerCount = tour.offers?.length ?? 0
    const categoryCount = tour.toursCategories?.length ?? 0
    const firstPhoto = tour.photos?.[0]

    return (
        <Card>
            <h4 style={{ marginTop: 0 }}>{tour.name || getString('Untitled tour')}</h4>
            {firstPhoto && (
                <div style={{ marginBottom: 12, maxWidth: '100%' }}>
                    <img
                        src={firstPhoto}
                        alt=""
                        style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 4 }}
                    />
                </div>
            )}
            <PInTableList fontSize="14px">
                <div><strong>{getString('External ID')}</strong>: {tour.externalId ?? '—'}</div>
                <div><strong>{getString('Last updated')}</strong>: {tour.lastEditDate ?? '—'}</div>
                <div><strong>{getString('City ID')}</strong>: {tour.cityId != null ? tour.cityId : '—'}</div>
                <div><strong>{getString('Currency')}</strong>: {tour.baseCurrencyCode ?? tour.externalCurrencyId ?? '—'}</div>
                <div><strong>{getString('Location')}</strong>: {[tour.locationAddress, tour.locationTiming].filter(Boolean).join(' · ') || '—'}</div>
                {(tour.locationLatitude != null || tour.locationLongitude != null) && (
                    <div>
                        <strong>{getString('Coordinates')}</strong>:{' '}
                        {tour.locationLatitude ?? '—'}, {tour.locationLongitude ?? '—'}
                    </div>
                )}
                <div><strong>{getString('Reviews')}</strong>: {tour.customReviewCount ?? 0} ({tour.customReviewsRating ?? '—'})</div>
                <div><strong>{getString('Categories')}</strong>: {categoryCount} {tour.toursCategories?.map(c => toursCategoriesHash.get(c) ?? 'Unknown category').join(', ')}</div>
                <div><strong>{getString('Photos')}</strong>: {photoCount}</div>
                <div><strong>{getString('Offers')}</strong>: {offerCount}</div>
            </PInTableList>
            {tour.description && (
                <div style={{ marginTop: 12 }}>
                    <strong>{getString('Description')}</strong>
                    <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 14 }}>{truncate(tour.description, 600)}</p>
                </div>
            )}
            {offerCount > 0 && tour.offers && (
                <div style={{ marginTop: 16 }}>
                    <strong style={{ fontSize: 15 }}>{getString('Offers')}</strong>
                    {tour.offers.map((offer, idx) => (
                        <OfferImportDetail key={offer.externalId ?? `offer-${idx}`} offer={offer} index={idx} getString={getString} />
                    ))}
                </div>
            )}
        </Card>
    )
}

const ToursImportPage = (props: Props) => {
    const [apiIntegrationsOptions, setApiIntegrationsOptions] = React.useState<SelectOptionModel[]>([])
    const [apiIntegrationId, setApiIntegrationId] = React.useState<string>('')
    const [tours, setTours] = React.useState<SelectOptionModel[]>([])
    const [selectedTourId, setSelectedTourId] = React.useState<string>('')
    const [selectedTourModel, setSelectedTourModel] = React.useState<TourImportModel | null>(null)
    const [syncedTours, setSyncedTours] = React.useState<Map<string, number>>(new Map<string, number>())
    const [tourLoading, setTourLoading] = React.useState(false)
    const [searchTourName, setSearchTourName] = React.useState<string>('')
    const toursCategoriesOptions = useSelector((state: LocalState) => state.toursCategories.options)
    const toursCategoriesHash = React.useMemo(() => {
        return toursCategoriesOptions.reduce((acc, curr) => {
            acc.set(curr.value as number, curr.label)
            return acc
        }, new Map<number, string>())
    }, [toursCategoriesOptions])
    React.useEffect(() => {
        dispatch(toursCategoriesActions.requestOptions())
    }, [])
    const Localization = useSelector((state: ApplicationState) => state.localization.locale)
    const dispatch = useDispatch()

    React.useEffect(() => {
        getApiIntegrations()
    }, [])

    React.useEffect(() => {
        setSelectedTourId('')
        setSelectedTourModel(null)
    }, [apiIntegrationId])

    React.useEffect(() => {
        if (apiIntegrationId) {
            getTours()
        }
    }, [apiIntegrationId])

    React.useEffect(() => {
        if (!selectedTourId || !apiIntegrationId) {
            setSelectedTourModel(null)
            setTourLoading(false)
            return
        }
        let cancelled = false
        setTourLoading(true)
            ; (async () => {
                const tourResponse = await ExternalIntegrationsApiService.getExternalTour(dispatch, selectedTourId, apiIntegrationId)
                if (!cancelled) {
                    if (tourResponse.IsOk && tourResponse.Content) {
                        setSelectedTourModel(tourResponse.Content)
                    } else {
                        setSelectedTourModel(null)
                    }
                    setTourLoading(false)
                }
            })()
        return () => {
            cancelled = true
        }
    }, [selectedTourId, apiIntegrationId, dispatch])

    const getApiIntegrations = async () => {
        const apiIntegrationsResponse = await ExternalIntegrationsApiService.getApiIntegrations(dispatch)
        if (apiIntegrationsResponse.IsOk && apiIntegrationsResponse.Content) {
            setApiIntegrationsOptions(apiIntegrationsResponse.Content)
        }
    }

    const filteredTours = React.useMemo(() => {
        return tours.filter(tour => tour.label.toLowerCase().includes(searchTourName.toLowerCase()))
    }, [searchTourName, tours])

    const getTours = async () => {
        const toursResponse = await ExternalIntegrationsApiService.getExternalTours(dispatch, apiIntegrationId)
        if (toursResponse.IsOk && toursResponse.Content) {
            setTours(toursResponse.Content.sort(defaultLabelSort))
        }
        const existingMappingsResponse = await ExternalIntegrationsApiService.getExternalToursMappings(dispatch, apiIntegrationId)
        if (existingMappingsResponse.IsOk && existingMappingsResponse.Content) {
            setSyncedTours(new Map<string, number>(existingMappingsResponse.Content.map(x => [x.externalId, x.tourId])))
        }
    }

    const handleTourClick = (tour: SelectOptionModel) => {
        setSelectedTourId(tour.value as string)
    }

    const handleImportToTourForm = async () => {
        if (selectedTourId && apiIntegrationId && selectedTourModel) {
            const response = await ToursApiService.import(dispatch, parseInt(apiIntegrationId), selectedTourModel)
            if (response.IsOk && response.Content) {
                const newSyncedTours = new Map<string, number>(syncedTours)
                newSyncedTours.set(selectedTourId, response.Content)
                setSyncedTours(newSyncedTours)
            }
        }
    }

    const getString = (key: string) => Localization.getString(key)

    return <Container>
        <Grid>
            <SectionTitle title={Localization.getString('Import Tours')} />
            <Row>
                <Col isNoMargin type="100">
                    <Card>
                        <h3>{Localization.getString('Import Tours')}</h3>
                        <SelectFormField options={apiIntegrationsOptions} value={apiIntegrationId} onChange={(e: SelectOptionModel) => setApiIntegrationId(e.value as string)} />
                        {apiIntegrationId && <FormFieldWrapper>
                            <FormFieldLabel label={Localization.getString('Search Tour Name')} />
                            <input className='form-control' value={searchTourName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTourName(e.target.value)} />
                        </FormFieldWrapper>}
                    </Card>

                </Col>
            </Row>
            {apiIntegrationId && (
                <Row>
                    <Col isNoMargin type="50">
                        <Card>
                            <PInTableList fontSize="16px">{Localization.getString('Tours')}</PInTableList>
                            {filteredTours.map(tour => (
                                <div
                                    className="clickable"
                                    style={selectedTourId === tour.value ? { background: 'rgba(0,0,0,0.06)', padding: '4px 8px', borderRadius: 4 } : { padding: '4px 8px' }}
                                    onClick={() => handleTourClick(tour)}
                                    key={tour.value?.toString()}
                                >
                                    <PInTableList style={{backgroundColor: syncedTours.has(tour.value as string) ? 'lightblue' : 'white'}} color='black' fontSize='14px'>
                                        {`${tour.label} (#${tour.value})`} 
                                        {syncedTours.has(tour.value as string) && <a href={`/tours/${syncedTours.get(tour.value as string)}`} target='_blank'><i className='fa fa-eye '/></a>}
                                    </PInTableList>
                                </div>
                            ))}
                        </Card>
                    </Col>
                    <Col isNoMargin type="50">
                        {tourLoading && (
                            <Card>
                                <PInTableList fontSize="16px">{Localization.getString('Loading…')}</PInTableList>
                            </Card>
                        )}
                        {!tourLoading && selectedTourModel && (
                            <>
                                <div style={{ marginBottom: 12 }}>
                                    {syncedTours.has(selectedTourId) ? <button className='button has-glow' onClick={handleImportToTourForm}>{Localization.getString('Update Tour Form')}</button> : <button className='button has-glow' onClick={handleImportToTourForm}>{Localization.getString('Import to Tour Form')}</button>}
                                    {syncedTours.has(selectedTourId) && <a href={`/tours/${syncedTours.get(selectedTourId)}`} target='_blank'><button className='button has-glow'>{Localization.getString('View Tour')}</button></a>}
                                </div>
                                <TourImportPreview tour={selectedTourModel} getString={getString} toursCategoriesHash={toursCategoriesHash} />
                            </>
                        )}
                        {!tourLoading && !selectedTourModel && selectedTourId && (
                            <Card>
                                <PInTableList fontSize="16px">{Localization.getString('Could not load tour details.')}</PInTableList>
                            </Card>
                        )}
                        {!tourLoading && !selectedTourId && (
                            <Card>
                                <PInTableList fontSize="16px">{Localization.getString('Select a tour to preview')}</PInTableList>
                            </Card>
                        )}
                    </Col>
                </Row>
            )}
        </Grid>
    </Container>
}
export default ToursImportPage
